import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileBarChart, Sparkles, Eye, EyeOff } from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

function fmtINR(v) {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (n >= 100000)  return `₹${(n / 100000).toFixed(2)} L`;
    if (n >= 1000)    return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
}

export default function HouseholdSummary() {
    const [data, setData] = useState(null);
    const [hide, setHide] = useState(false);

    useEffect(() => {
        api.get("/household/summary").then(r => setData(r.data)).catch(() => setData(null));
        // Hide values pref — keeps a simple client-side toggle here.
        setHide(localStorage.getItem("vault.hide_values") === "1");
    }, []);

    const toggleHide = () => {
        const next = !hide;
        setHide(next);
        localStorage.setItem("vault.hide_values", next ? "1" : "0");
    };
    const V = (v) => hide ? "•••••" : fmtINR(v);

    return (
        <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="household-summary-page">
            <HouseholdNav />
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <FileBarChart size={12} /> Household VAULT Summary
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                        One page. Everything shared.
                    </h1>
                </div>
                <button onClick={toggleHide}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold border border-stone-300 rounded-full px-3 py-1.5"
                        data-testid="summary-hide-values">
                    {hide ? <EyeOff size={12} /> : <Eye size={12} />} {hide ? "Values hidden" : "Values shown"}
                </button>
            </div>

            {data && (
                <>
                    <section className="bg-white border border-stone-200 rounded-2xl p-5 mb-4">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Shared position</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {["assets_value", "investments_value", "liabilities_value", "household_net_worth"].map(k => (
                                <div key={k} className="border border-stone-200 rounded-lg p-3 bg-stone-50/50" data-testid={`summary-${k}`}>
                                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{k.replace(/_/g, " ")}</div>
                                    <div className="text-2xl font-display tabular-nums text-stone-900 mt-1">{V(data.sections.shared_position[k])}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        {[
                            ["upcoming_obligations", "Upcoming · 30d"],
                            ["shared_subscriptions", "Shared subscriptions"],
                            ["shared_documents",      "Shared documents"],
                            ["shared_goals_open",     "Shared goals · open"],
                            ["records_needing_ownership_label", "Records needing labels"],
                        ].map(([k, l]) => (
                            <div key={k} className="rounded-xl border border-stone-200 bg-white p-3.5" data-testid={`summary-${k}`}>
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{l}</div>
                                <div className="text-2xl font-display tabular-nums text-stone-900 mt-1">{data.sections[k] ?? 0}</div>
                            </div>
                        ))}
                    </div>

                    {(data.wins || []).length > 0 && (
                        <section className="bg-emerald-50/40 border border-emerald-200 rounded-2xl p-5 mb-4" data-testid="summary-wins">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles size={12} className="text-emerald-800" />
                                <div className="text-[10.5px] uppercase tracking-widest text-emerald-800 font-semibold">Shared wins</div>
                            </div>
                            <ul className="space-y-1">
                                {data.wins.map(w => (
                                    <li key={w} className="text-[13px] text-emerald-900 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                                        {w}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <div className="text-[11px] text-stone-500 max-w-3xl">{data.note}</div>
                </>
            )}
        </div>
    );
}
